import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AuthCardLayout } from '@/components/AuthCardLayout'
import { FormPasswordInput, SubmitButton } from '@/components/form'
import { usersService } from '@/services/users'
import { ROUTES } from '@/constants/routes'
import { getApiErrorMessage } from '@/utils/errors'

interface ResetPasswordLocationState {
  email: string
  code: string
}

export function ResetPasswordSetPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as ResetPasswordLocationState | null
  const email = searchParams.get('email') ?? state?.email ?? ''
  const code = searchParams.get('code') ?? state?.code ?? ''

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldError, setFieldError] = useState<string | null>(null)

  useEffect(() => {
    if (!email || !code) {
      navigate(ROUTES.FORGOT_PASSWORD, { replace: true })
    }
  }, [email, code, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setFieldError(null)
    if (newPassword !== confirmPassword) {
      setFieldError(t('resetPasswordSet.errors.mismatch'))
      return
    }
    if (!email || !code) return
    setIsLoading(true)
    try {
      await usersService.confirmPasswordReset({
        email,
        code,
        new_password: newPassword,
      })
      navigate(ROUTES.LOGIN, {
        replace: true,
        state: { message: t('resetPasswordSet.successMessage') },
      })
    } catch (err) {
      const message = getApiErrorMessage(err, t('resetPasswordSet.errors.invalidLink'))
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  if (!email || !code) {
    return null
  }

  const backToVerifyUrl =
    error != null ? `${ROUTES.RESET_PASSWORD_VERIFY}?email=${encodeURIComponent(email)}` : undefined

  return (
    <AuthCardLayout title={t('resetPasswordSet.title')} backTo={backToVerifyUrl}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {(error || fieldError) && (
          <div
            className="flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4"
            role="alert"
          >
            <p className="text-sm text-red-600">{error ?? fieldError}</p>
          </div>
        )}
        <FormPasswordInput
          id="new-password"
          label={t('resetPasswordSet.newPasswordLabel')}
          required
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
          placeholder={t('resetPasswordSet.newPasswordPlaceholder')}
          autoComplete="new-password"
          disabled={isLoading}
          showPasswordLabel={t('login.showPassword')}
          hidePasswordLabel={t('login.hidePassword')}
          hint={t('resetPasswordSet.passwordHint')}
        />
        <FormPasswordInput
          id="confirm-password"
          label={t('resetPasswordSet.confirmPasswordLabel')}
          required
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          placeholder={t('resetPasswordSet.confirmPasswordPlaceholder')}
          autoComplete="new-password"
          disabled={isLoading}
          showPasswordLabel={t('login.showPassword')}
          hidePasswordLabel={t('login.hidePassword')}
        />
        <SubmitButton
          fullWidth
          showIcon={false}
          isLoading={isLoading}
          loadingLabel={t('resetPasswordSet.submitting')}
        >
          {t('resetPasswordSet.submitButton')}
        </SubmitButton>
      </form>
    </AuthCardLayout>
  )
}
